import clsx from 'clsx';
import React from 'react';

import Clipper from '@components/Clipper';
import Comments from '@components/Comments';
import VerticalAdvertisement from '@components/FeaturedPartnerAd/Vertical';
import type { MidArticleEventProps } from '@components/pages/Article/MidArticle/Event/interfaces';
import type { Env } from '@contexts/AppContext';
import { useArticleCommentsSummary } from '@hooks/useArticleCommentsSummary';
import useCurrentUser from '@hooks/useCurrentUser';
import useNativeAppVersionCheck from '@hooks/useNativeAppVersionCheck';
import useServerSideRendering from '@hooks/useServerSideRendering';
import type { Article, Pickup, ScriptList } from '@interfaces/homepage';
import { TrackingLocation } from '@tracking';
import { CommentableType } from '@utils/backend-api/comments';
import { getArticleImgSrc } from '@utils/images';

import ArticlePickups from '../ArticlePickups';
import Body from '../Body';
import EndOfArticleDeepResearch from '../EndOfArticle/DeepResearch';
import { type AccessState, MidArticleUnitTypes } from '../interfaces';

interface DataContentProps {
  access: AccessState;
  addendum: Article['addendum'];
  advertisement?: Article['advertisement'];
  authors: Article['authors'];
  bodyClassName?: string;
  env: Env;
  freeBlurb: Article['freeBlurb'];
  fullText: Article['fullText'];
  highlight?: string;
  id: number;
  midArticleUnitType?: MidArticleUnitTypes;
  newsletterId?: string | number;
  picture?: string;
  pickups?: Pickup[];
  relatedEvent?: MidArticleEventProps;
  scriptList?: ScriptList[];
  takeaway: Article['takeaway'];
}

const DataContent = ({
  access,
  addendum,
  advertisement,
  authors,
  bodyClassName,
  env,
  freeBlurb,
  fullText,
  highlight,
  id,
  picture,
  pickups,
  relatedEvent,
  scriptList,
  takeaway,
}: DataContentProps): JSX.Element => {
  const hydrated = useServerSideRendering();
  const { currentUser } = useCurrentUser();
  const { isApp3_0OrGreater, isNativeApp } = useNativeAppVersionCheck();
  const { commentsCount } = useArticleCommentsSummary(id);
  const isLegacyNativeApp = isNativeApp && !isApp3_0OrGreater;
  const clipperProps = {
    contentSelector: '.clipperable-content',
    contentImgixUrl: getArticleImgSrc(id, picture, env),
    logoUrl: `${env.IMGIX_URL}/logos/theinformation/the-information-logo-wordmark.svg`,
  };

  return (
    <>
      <div
        className={clsx('flex flex-col md:flex-row md:gap-72 xl:gap-112', {
          'mb-80': access === 'locked',
        })}
      >
        <div className="flex flex-1 flex-col">
          <div className="md:ml-[60px] xl:ml-[84px]">
            <Body
              access={access}
              addendum={addendum}
              articleId={id}
              authors={authors}
              className={bodyClassName}
              freeBlurb={freeBlurb}
              fullText={fullText}
              highlight={highlight}
              relatedEvent={relatedEvent}
              scriptList={scriptList}
              takeaway={takeaway}
            />
            {access !== 'locked' && isLegacyNativeApp && <EndOfArticleDeepResearch id={id} />}
            {access !== 'locked' && pickups?.length > 0 && <ArticlePickups pickups={pickups} />}
          </div>
        </div>
        <div className="flex-shrink-0 md:w-[195px] lg:w-[260px] print:hidden">
          {advertisement && (
            <VerticalAdvertisement
              advertisement={advertisement}
              className="mt-64 hidden md:block"
              trackingLocation={TrackingLocation.SideFeed}
            />
          )}
        </div>
      </div>
      {hydrated && currentUser?.can_view_comments && (
        <div className="mr-0 mt-80 sm:mt-112 md:mr-[267px] lg:mr-[372px] print:hidden">
          <Comments
            commentableId={id}
            commentableType={CommentableType.Article}
            commentsCount={commentsCount}
          />
        </div>
      )}
      {access !== 'locked' && advertisement && (
        <div className="mx-48">
          <VerticalAdvertisement
            advertisement={advertisement}
            className="mt-48 block md:hidden"
            trackingLocation={TrackingLocation.ContentBody}
          />
        </div>
      )}
      {hydrated && <Clipper {...clipperProps} env={env} />}
    </>
  );
};

export default DataContent;
